import React, { useState, memo, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlan } from '@/contexts/PlanContext';
import { usePaywall } from '@/contexts/PaywallContext';
import { prefetch } from '@/utils/cache';
import { dashboardAPI, onboardingAPI, projectsAPI, usersAPI, receiptsAPI, timeEntriesAPI, companyAPI, authAPI } from '@/services/api';
import FloatingActionButton from './FloatingActionButton';
import LanguageToggle from './LanguageToggle';
import CompanySwitcher from './CompanySwitcher';
import PaywallBanner from './PaywallBanner';
import PaywallModal from './PaywallModal';
import TrialBar, { TRIAL_STATUSES } from './TrialBar';
import { useI18n } from '@/contexts/I18nContext';
import { useReminders, useReminderSuggestion } from '@/hooks/useReminders';
import {
  Home, Clock, Receipt, FolderOpen, FileText, Tag, Menu,
  LogOut, Building2, Users, Download, Settings, MessageSquare, Mail,
  ChevronRight, HelpCircle, AlertTriangle, ArrowUpRight,
  CreditCard, ShieldAlert, Timer, User, BarChart3, X as XIcon,
  FileCheck, Calculator, MailCheck, Sparkles, UserCog
} from 'lucide-react';

const prefetchers = {
  '/dashboard': () => prefetch('dashboard', async () => {
    const [d, o] = await Promise.all([dashboardAPI.get(), onboardingAPI.getStatus()]);
    return { dashboard: d.data, workerData: null };
  }),
  '/time': () => {
    const today = new Date().toISOString().split('T')[0];
    prefetch(`time-entries:${today}`, async () => {
      const [e, p, s] = await Promise.all([
        timeEntriesAPI.list({ date: today }),
        projectsAPI.list(true),
        companyAPI.getSettings().catch(() => ({ data: {} }))
      ]);
      const entryList = e.data.entries || [];
      const hoursMap = {};
      const rangesMap = {};
      entryList.forEach(entry => {
        hoursMap[entry.project_id] = (hoursMap[entry.project_id] || 0) + entry.hours;
        if (entry.start_time && entry.end_time) {
          rangesMap[entry.project_id] = { start: entry.start_time, end: entry.end_time };
        }
      });
      return { entries: entryList, projects: p.data || [], timeMode: s.data?.time_logging_mode || 'numeric', hoursMap, rangesMap };
    });
  },
  '/projects': () => prefetch('projects', async () => {
    const [p, u] = await Promise.allSettled([projectsAPI.list(false), usersAPI.list()]);
    return {
      projects: p.status === 'fulfilled' ? (p.value.data || []) : [],
      users: u.status === 'fulfilled' ? (u.value.data || []) : [],
    };
  }),
  '/receipts': () => prefetch('receipts:all', async () => {
    const [r, p] = await Promise.all([receiptsAPI.list({}), projectsAPI.list(true)]);
    return { receipts: r.data.items || [], projects: p.data || [] };
  }),
};

const SettingsMenu = ({ onClose, onLogout, user, navigate, t, language, billingVisible }) => {
  const role = user?.role;
  // Company settings access by role
  const canSeeCompanySettings = role === 'owner' || role === 'admin' || role === 'bookkeeper' || role === 'manager';
  // Export access
  const canExport = role !== 'worker';

  const menuSections = [
    {
      label: t('settings_section_me'),
      items: [
        { icon: UserCog, label: t('my_settings'), path: '/account', show: true },
      ]
    },
    {
      label: t('settings_section_company'),
      items: [
        { icon: Building2, label: t('settings_company'), path: '/settings', show: canSeeCompanySettings },
        { icon: CreditCard, label: language === 'is' ? 'Áskrift' : 'Subscription', path: '/settings/billing', show: role === 'owner' || role === 'admin' },
      ]
    },
    {
      label: t('settings_section_admin'),
      items: [
        { icon: Download, label: t('settings_export'), path: '/reports', show: canExport },
      ]
    },
    {
      label: t('settings_section_help'),
      items: [
        { icon: HelpCircle, label: t('settings_help'), path: '/instructions', show: true },
        { icon: MessageSquare, label: t('settings_feedback_title'), path: '/feedback', show: true },
        { icon: Mail, label: language === 'is' ? 'Hafa samband' : 'Contact us', path: 'mailto:rektra@rektra.is', show: true },
      ]
    },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="relative z-50 bg-card border border-border rounded-xl shadow-xl overflow-hidden" data-testid="settings-menu">
        {/* User Info Header */}
        <div className="p-4 border-b border-border bg-background-elevated/50">
          <p className="font-medium text-sm">{user?.name}</p>
          <p className="text-xs text-foreground-muted">{user?.email}</p>
          <p className="text-xs text-foreground-muted mt-0.5">{t('role_' + user?.role)} &middot; {user?.company_name}</p>
        </div>

        {/* Menu Sections */}
        <div className="py-1">
          {menuSections.map((section, si) => {
            const visibleItems = section.items.filter(i => i.show);
            if (visibleItems.length === 0) return null;
            return (
              <div key={si}>
                <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-foreground-muted font-semibold">{section.label}</p>
                {visibleItems.map((item, ii) => (
                  <button
                    key={ii}
                    onClick={() => {
                      // Don't call onClose() here — it can race with navigate()
                      // on iOS PWA standalone mode. The route-change useEffect
                      // in Layout closes the menu once the URL updates.
                      // mailto: targets stay as-is (no route change).
                      if (item.path.startsWith('mailto:')) {
                        onClose();
                        window.location.href = item.path;
                      } else {
                        navigate(item.path);
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-card-hover transition-colors text-left"
                    data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon size={15} className="text-foreground-muted" />
                    <span className="flex-1">{item.label}</span>
                    <ChevronRight size={14} className="text-foreground-muted/50" />
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        {/* Logout */}
        <div className="border-t border-border">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-error hover:bg-error/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={15} />
            {t('logout')}
          </button>
        </div>
      </div>
    </>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const {
    overLimit, maxUsers, currentUsers, planName,
    isPastDue, isRestricted, gracePeriodDaysRemaining,
    subscriptionStatus, billingVisible, isTrialExpired, trialDaysRemaining,
    isPretrial, needsCardSetup, emailVerified, hasInvoiceHistory,
    isGrandfatherTrial,
    autoActivate, autoActivatePlanName, autoActivateMonthlyIsk, firstInvoiceDate,
  } = usePlan() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const [showMenu, setShowMenu] = useState(false);
  const [trialBannerDismissedOn, setTrialBannerDismissedOn] = useState(() => {
    try { return localStorage.getItem('rektra.trialBannerDismissedOn'); } catch { return null; }
  });
  const todayStamp = new Date().toISOString().slice(0, 10);
  const dismissTrialBanner = () => {
    try { localStorage.setItem('rektra.trialBannerDismissedOn', todayStamp); } catch {}
    setTrialBannerDismissedOn(todayStamp);
  };
  const { showPaywall, triggerPaywall, dismissPaywall } = usePaywall();
  const isOwnerOrAdmin = ['owner', 'admin'].includes(user?.role);

  // Register the paywall trigger on window so api.js interceptor can call it.
  // Only owners/admins can act on billing, so employees never get the modal —
  // they'd just see a scary "áskrift krafist" dialog they can't resolve.
  useEffect(() => {
    window.__triggerPaywall = isOwnerOrAdmin ? triggerPaywall : (() => {});
    return () => { window.__triggerPaywall = null; };
  }, [triggerPaywall, isOwnerOrAdmin]);

  // Scroll main content to top on route change
  useEffect(() => {
    const main = document.querySelector('.main-content');
    if (main) main.scrollTo(0, 0);
  }, [location.pathname]);

  // Auto-close mobile overlays whenever the route changes.
  //
  // ─── Why this useEffect, and NOT onClick on each <Link> ────────────────
  // Previously each <Link> in the "Meira" bottom sheet also had
  //   onClick={() => setShowMoreMenu(false)}
  // which worked on desktop but broke the menu on iOS PWA standalone mode:
  // React fires the user-supplied onClick before react-router's internal
  // navigation handler. On iOS Safari standalone the setState → immediate
  // re-render unmounted the <Link> before react-router got to fire its
  // history.push, so the URL never changed and the sheet appeared to
  // "just close". See memory/INCIDENTS.md — 2026-04-24 ios-pwa-meira fix.
  //
  // Driving close off `location.pathname` means the Link gets to navigate
  // unimpeded; we then close on the next render tick after the route has
  // already changed. Works in all browsers, covers deep links and history
  // back/forward for free.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('MEIRA_ROUTE_CHANGE', location.pathname, Date.now());
    setShowMoreMenu(false);
    setShowMenu(false);
  }, [location.pathname]);

  // Client-side hour logging reminders
  useReminders();
  const suggestion = useReminderSuggestion();
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const onChange = (e) => setIsNarrowViewport(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Show trial warning banner. Non-annoying rules:
  //  - Only at ≤6 days remaining (nothing earlier)
  //  - Dismissible once per calendar day (stored in localStorage)
  //  - At ≤2 days remaining → urgent (red), cannot be dismissed
  const daysLeft = trialDaysRemaining;
  // The neutral trial bar: trialing companies only. Never for paying,
  // exempt or grandfathered companies (iter323 — replaced the beta banner).
  const showTrialBar = isOwnerOrAdmin
    && TRIAL_STATUSES.includes(subscriptionStatus)
    && !isGrandfatherTrial
    && !isTrialExpired
    && !isRestricted;
  const trialWindowEligible = billingVisible && isOwnerOrAdmin
    && subscriptionStatus === 'trial'
    && !isTrialExpired
    && daysLeft != null
    && daysLeft >= 0
    && daysLeft <= 6;
  const isFinalTrialWarning = trialWindowEligible && daysLeft <= 2;
  const dismissedToday = trialBannerDismissedOn === todayStamp;
  const showTrialWarning = trialWindowEligible && (isFinalTrialWarning || !dismissedToday);

  // Handle tab param for settings page
  React.useEffect(() => {
    // Handled by CompanySettings via URL params
  }, [location]);

  const handleLogout = async () => {
    setShowMenu(false);
    await logout();
    navigate('/login');
  };

  const handleNavClick = useCallback((path) => {
    const fn = prefetchers[path];
    if (fn) fn();
  }, []);

  // Email-confirmation resend (used by the global banner)
  const isWorker = user?.role === 'worker';
  const is = language === 'is';
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationJustSent, setVerificationJustSent] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const handleResendVerification = useCallback(async () => {
    if (resendingVerification) return;
    setResendingVerification(true);
    setVerificationError(null);
    try {
      await authAPI.sendVerification();
      setVerificationJustSent(true);
      setTimeout(() => setVerificationJustSent(false), 5000);
    } catch (e) {
      // Surface the real error so users never click and silently wait for
      // an email that's never coming (Resend rejection, rate-limit, etc.).
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || '';
      let msg;
      if (status === 429) {
        msg = is
          ? 'Of margar beiðnir. Reyndu aftur eftir 24 klst.'
          : 'Too many requests. Try again in 24 hours.';
      } else if (detail === 'email_send_failed' || status === 502) {
        msg = is
          ? 'Tölvupóstþjónusta svaraði ekki. Reyndu aftur eftir nokkrar mínútur eða hafðu samband við stuðning.'
          : 'Email service did not respond. Try again in a few minutes or contact support.';
      } else {
        msg = is
          ? `Sending mistókst${detail ? ` (${detail})` : ''}.`
          : `Send failed${detail ? ` (${detail})` : ''}.`;
      }
      setVerificationError(msg);
      // Auto-clear after 8s so it doesn't stick around forever
      setTimeout(() => setVerificationError(null), 8000);
    } finally {
      setResendingVerification(false);
    }
  }, [resendingVerification, is]);

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const desktopMoreRef = useRef(null);

  // Close desktop "More" dropdown when user clicks/taps anywhere outside it or presses Escape.
  //
  // ─── Why scope check matters (2026-04-24 ios-pwa Meira bug fix v2) ────
  // This capture-phase `touchstart` handler previously fired for EVERY tap
  // on mobile, including taps on <Link> items inside the mobile bottom
  // sheet, because the mobile overlay isn't inside `desktopMoreRef`. That
  // closed the sheet before react-router could navigate. We now early-exit
  // when the tap lands inside the mobile overlay (data-testid "more-menu-
  // overlay") so only genuine outside-taps close the dropdown.
  useEffect(() => {
    if (!showMoreMenu) return;
    const handlePointer = (e) => {
      // Ignore taps inside the mobile bottom sheet (its own backdrop + the
      // route-change useEffect handle closing). Only the DESKTOP dropdown
      // should be affected by this outside-click handler.
      const mobileOverlay = document.querySelector('[data-testid="more-menu-overlay"]');
      if (mobileOverlay && mobileOverlay.contains(e.target)) {
        // eslint-disable-next-line no-console
        console.log('MEIRA_OUTSIDE_IGNORED', 'tap-inside-mobile-overlay', Date.now());
        return;
      }
      if (desktopMoreRef.current && !desktopMoreRef.current.contains(e.target)) {
        // eslint-disable-next-line no-console
        console.log('MEIRA_OUTSIDE_CLOSE', e.type, Date.now());
        setShowMoreMenu(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setShowMoreMenu(false);
    };
    // Use capture phase so we beat any child stopPropagation calls
    document.addEventListener('mousedown', handlePointer, true);
    document.addEventListener('touchstart', handlePointer, true);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer, true);
      document.removeEventListener('touchstart', handlePointer, true);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showMoreMenu]);

  const allNavItems = [
    { path: '/dashboard', icon: Home, label: t('nav_home') },
    { path: '/time', icon: Clock, label: t('nav_time') },
    { path: '/receipts', icon: Receipt, label: t('nav_receipts') },
    { path: '/projects', icon: FolderOpen, label: t('nav_projects') },
    { path: '/quotes', icon: FileCheck, label: is ? 'Tilboð' : 'Quotes', hideForWorker: true },
    { path: '__menu__', icon: Menu, label: is ? 'Meira' : 'More', hideForWorker: true, isMenu: true },
  ];

  // Pre-launch focus: keep the "More" menu lean and aimed at revenue-relevant
  // surfaces only. The following entries are hidden until we have paying
  // customers actually asking for them — the routes still work via deep link,
  // we just remove the navigation entry points so day-1 users see a tight,
  // simple product (Tímar → Reikningar → Áskrift).
  //   - /efnismagntaka (AI material takeoff)
  //   - /financial-overview (financial overview)
  // Owner-side suppliers + bookkeeping settings are hidden in CompanySettings.js.
  const menuPages = [
    { path: '/categories', icon: Tag, label: t('nav_categories') },
    { path: '/customers', icon: Users, label: is ? 'Viðskiptavinir' : 'Customers' },
    { path: '/unit-prices', icon: Calculator, label: is ? 'Einingarverð' : 'Unit Prices' },
    { path: '/reports/hours', icon: Timer, label: is ? 'Tímayfirsýn' : 'Hours Overview' },
    { path: '/reports', icon: Download, label: is ? 'Skýrslur' : 'Reports' },
  ];

  const navItems = isWorker ? allNavItems.filter(n => !n.hideForWorker) : allNavItems;

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    if (path === '/reports') return location.pathname === '/reports';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="pwa-shell bg-background">
      {/* ========== DESKTOP: Minimal Left Rail ========== */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-20 lg:flex-col border-r border-border bg-background-elevated">
        <div className="flex items-center justify-center py-5 border-b border-border">
          <img src="/logos/favicon-black.png" alt="REKTRA" className="w-10 h-10 object-contain" data-testid="sidebar-logo" />
        </div>

        <nav className="flex-1 flex flex-col items-center py-4 gap-1">
          {navItems.filter(item => !item.isMenu).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`w-12 h-12 flex items-center justify-center rounded-xl transition-colors ${
                isActive(item.path) ? 'bg-accent/10 text-accent' : 'text-foreground-muted hover:text-foreground hover:bg-card'
              }`}
              title={item.label}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={22} strokeWidth={isActive(item.path) ? 2.5 : 2} />
            </Link>
          ))}
          {/* Desktop: "More" button with dropdown */}
          {!isWorker && (
            <div className="mt-2 pt-2 border-t border-border w-12 flex flex-col items-center relative" ref={desktopMoreRef}>
              <button
                onClick={() => setShowMoreMenu(p => !p)}
                className={`w-12 h-12 flex items-center justify-center rounded-xl transition-colors ${
                  showMoreMenu || menuPages.some(mp => location.pathname.startsWith(mp.path))
                    ? 'bg-accent/10 text-accent'
                    : 'text-foreground-muted hover:text-foreground hover:bg-card'
                }`}
                title={is ? 'Meira' : 'More'}
                data-testid="nav-desktop-more"
              >
                <Menu size={20} />
              </button>
              {showMoreMenu && (
                <div className="absolute left-full top-0 ml-2 w-56 bg-card border border-border rounded-xl shadow-lg py-2 z-50" data-testid="desktop-more-dropdown">
                  {menuPages.map((mp) => (
                    <Link
                      key={mp.path}
                      to={mp.path}
                      onClick={() => setShowMoreMenu(false)}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        location.pathname.startsWith(mp.path) ? 'text-accent bg-accent/5' : 'text-foreground hover:bg-card-hover'
                      }`}
                      data-testid={`desktop-menu-${mp.path.replace(/\//g, '-').slice(1)}`}
                    >
                      <mp.icon size={16} />
                      <span className="font-medium">{mp.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Language Toggle */}
        <div className="px-3 py-2 flex justify-center">
          <LanguageToggle />
        </div>

        {/* Company Switcher (desktop, above profile) */}
        {user?.memberships?.length > 1 && (
          <div className="px-2 pb-1">
            <CompanySwitcher compact />
          </div>
        )}

        {/* Profile/Settings Button */}
        <div className="p-3 border-t border-border relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`w-full flex items-center justify-center gap-2 px-2 py-2.5 rounded-xl border transition-colors ${
              showMenu ? 'bg-accent/10 border-accent/30 text-accent' : 'border-border hover:bg-card hover:border-border'
            }`}
            data-testid="profile-btn"
            style={{ minHeight: '44px' }}
          >
            <div className="w-7 h-7 bg-card rounded-full flex items-center justify-center text-xs font-medium border border-border flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                user?.name?.[0]?.toUpperCase()
              )}
            </div>
          </button>
          
          {showMenu && (
            <div className="absolute left-full bottom-full ml-2 mb-2 w-64" data-testid="desktop-settings-dropdown">
              <SettingsMenu
                onClose={() => setShowMenu(false)}
                onLogout={handleLogout}
                user={user}
                navigate={navigate}
                t={t}
                language={language}
                billingVisible={billingVisible}
              />
            </div>
          )}
        </div>
      </aside>

      {/* ========== MOBILE: Top Header ========== */}
      <header className="mobile-header flex items-center justify-between lg:hidden bg-background-elevated border-b border-border">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <img src="/logos/favicon-black.png" alt="REKTRA" className="w-8 h-8 object-contain flex-shrink-0" data-testid="mobile-header-logo" />
          {user?.memberships?.length > 1 ? (
            <CompanySwitcher />
          ) : (
            <div className="min-w-0">
              <p className="text-xs font-medium truncate" data-testid="mobile-company-name">{user?.company_name || 'REKTRA'}</p>
              <p className="text-[10px] text-foreground-muted capitalize">{t('role_' + user?.role)}</p>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-colors ${
              showMenu ? 'bg-accent/10 border-accent/30' : 'border-border hover:bg-card'
            }`}
            style={{ minHeight: '44px' }}
            data-testid="mobile-profile-btn"
          >
            <Settings size={16} className="flex-shrink-0" />
            <span className="text-xs font-medium">{t('settings')}</span>
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-12 w-64">
              <SettingsMenu
                onClose={() => setShowMenu(false)}
                onLogout={handleLogout}
                user={user}
                navigate={navigate}
                t={t}
                language={language}
                billingVisible={billingVisible}
              />
            </div>
          )}
          </div>
        </div>
      </header>

      {/* ========== MOBILE: Bottom Navigation ========== */}
      <nav className="mobile-bottom-nav flex items-center justify-around lg:hidden bg-background-elevated border-t border-border">
        {navItems.map((item) => {
          if (item.isMenu) {
            const isMenuActive = menuPages.some(mp => location.pathname.startsWith(mp.path));
            return (
              <button
                key="__menu__"
                onClick={() => setShowMoreMenu(true)}
                className={`flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-xl transition-colors ${
                  isMenuActive ? 'text-accent' : 'text-foreground-muted'
                }`}
                data-testid="mobile-nav-more"
              >
                <item.icon size={22} strokeWidth={isMenuActive ? 2.5 : 2} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`flex flex-col items-center justify-center gap-1 w-16 h-14 rounded-xl transition-colors ${
                isActive(item.path) ? 'text-accent' : 'text-foreground-muted'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={22} strokeWidth={isActive(item.path) ? 2.5 : 2} />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* ========== "More" Menu Overlay ========== */}
      {showMoreMenu && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:hidden" onClick={() => { console.log('MEIRA_SHEET_CLOSE', 'backdrop', Date.now()); setShowMoreMenu(false); }} data-testid="more-menu-overlay">
          <div className="w-full bg-card rounded-t-2xl p-5 space-y-1 animate-in slide-in-from-bottom" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">{is ? 'Meira' : 'More'}</h3>
              <button onClick={() => { console.log('MEIRA_SHEET_CLOSE', 'x-button', Date.now()); setShowMoreMenu(false); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-card-hover">
                <XIcon size={18} />
              </button>
            </div>
            {menuPages.map((mp) => (
              <Link
                key={mp.path}
                to={mp.path}
                onClick={() => {
                  // eslint-disable-next-line no-console
                  console.log('MEIRA_TAP', mp.path, Date.now());
                }}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  location.pathname.startsWith(mp.path) ? 'bg-accent/10 text-accent' : 'hover:bg-card-hover text-foreground'
                }`}
                data-testid={`more-menu-${mp.path.replace(/\//g, '-').slice(1)}`}
              >
                <mp.icon size={18} />
                <span className="text-sm font-medium">{mp.label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ========== Main Content ========== */}
      <main className="main-content lg:pl-20 lg:pt-0 lg:pb-8">
        {/* Beta banner removed (iter323): it contradicted the paid trial.
            The trial bar below is now the only trial-state messaging. */}
        {/* Pre-trial paywall banner */}
        {/* PaywallBanner hidden until Konto SaaS billing UI is ready */}
        {/* {isPretrial && <PaywallBanner />} */}

        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
          {/* ─── ONE banner at a time (iter323) ──────────────────────────
              Stacked banners pushed the actual page below the fold on
              mobile. `banner` resolves to a single slot, in priority
              order: trial bar, then email verification, then the billing
              blockers in descending severity. */}
          {(() => {
            const showEmailVerify = isOwnerOrAdmin && emailVerified === false;
            const showTrialExpired = billingVisible && isTrialExpired && isOwnerOrAdmin && !isPretrial;
            const showRestricted = billingVisible && isRestricted && isOwnerOrAdmin && !isPretrial;
            const showPastDue = billingVisible && isPastDue && !isRestricted && isOwnerOrAdmin;
            const showCardSetup = billingVisible && isOwnerOrAdmin && (needsCardSetup || isPretrial)
              && !isTrialExpired && !isPastDue && !isRestricted
              && !location.pathname.startsWith('/projects/');
            const showPlanLimit = billingVisible && overLimit && isOwnerOrAdmin;

            if (showEmailVerify) return (
              <>
                <div
                  className="mb-3 flex items-center gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2"
                  data-testid="email-verify-banner"
                >
                  <MailCheck size={16} className="text-amber-400 flex-shrink-0" />
                  <p className="text-sm text-amber-100 flex-1 min-w-0">
                    {verificationJustSent
                      ? (is ? 'Sendi nýjan póst.' : 'Fresh email sent.')
                      : (is
                        ? 'Staðfestu netfangið þitt'
                        : 'Confirm your email')}
                  </p>
                  {!verificationJustSent && (
                    <button
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 transition-colors whitespace-nowrap disabled:opacity-50"
                      data-testid="email-verify-resend-btn"
                    >
                      {resendingVerification
                        ? <Timer size={11} className="animate-spin" />
                        : <Mail size={11} />}
                      {is ? 'Senda aftur' : 'Resend'}
                    </button>
                  )}
                </div>
                {verificationError && (
                  <div
                    className="mb-3 flex items-start gap-2.5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2"
                    data-testid="email-verify-error-banner"
                  >
                    <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-200 flex-1 leading-snug">{verificationError}</p>
                  </div>
                )}
              </>
            );

            if (showTrialBar) return (
              <TrialBar
                daysLeft={daysLeft}
                autoActivate={autoActivate}
                planName={autoActivatePlanName}
                monthlyIsk={autoActivateMonthlyIsk}
                firstInvoiceDate={firstInvoiceDate}
              />
            );

            if (showTrialExpired) return (
              <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2" data-testid="trial-expired-banner">
                <ShieldAlert size={16} className="text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-200 flex-1 min-w-0 truncate font-medium">
                  {t('trial_expired_message')}
                </p>
                <Link to="/settings/billing" className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors whitespace-nowrap" data-testid="trial-expired-upgrade-btn">
                  {t('trial_activate_subscription')}
                </Link>
              </div>
            );

            if (showRestricted) return (
              <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2" data-testid="billing-restricted-banner">
                <ShieldAlert size={16} className="text-red-400 flex-shrink-0" />
                <p className="text-sm font-medium text-red-200 flex-1 min-w-0 truncate">
                  {hasInvoiceHistory ? t('billing_restricted_message') : t('billing_setup_required_message')}
                </p>
                <Link
                  to="/settings/billing"
                  className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors whitespace-nowrap"
                  data-testid="billing-update-payment-btn"
                >
                  {hasInvoiceHistory ? t('billing_update_payment') : t('billing_open_settings')}
                </Link>
              </div>
            );

            if (showPastDue) return (
              <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="billing-past-due-banner">
                <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-100 flex-1 min-w-0 truncate">
                  <span className="font-semibold">
                    {language === 'en' ? 'Invoice unpaid' : 'Reikningur ógreiddur'}
                  </span>
                  <span className="hidden sm:inline text-amber-200/80 ml-2 text-xs">
                    {language === 'en'
                      ? `${gracePeriodDaysRemaining ?? 0}d remaining`
                      : `${gracePeriodDaysRemaining ?? 0} dagar eftir`}
                  </span>
                </p>
                <Link to="/settings/billing" className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 transition-colors whitespace-nowrap" data-testid="billing-fix-payment-btn">
                  {t('billing_fix_payment')}
                </Link>
              </div>
            );

            if (showCardSetup) return (
              <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2" data-testid="card-setup-banner">
                <CreditCard size={16} className="text-accent flex-shrink-0" />
                <p className="text-sm text-foreground flex-1 min-w-0 truncate">
                  <span className="font-medium">
                    {language === 'is' ? 'Veldu áskrift til að halda áfram' : 'Choose a plan to continue'}
                  </span>
                  <span className="hidden sm:inline text-foreground-muted ml-2 text-xs">
                    {language === 'is'
                      ? 'mánaðarlegur reikningur og krafa í heimabanka.'
                      : 'monthly invoice + bank claim.'}
                  </span>
                </p>
                <Link
                  to="/settings/billing"
                  className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md bg-accent text-background hover:bg-accent/90 transition-colors whitespace-nowrap"
                  data-testid="card-setup-btn"
                >
                  {language === 'is' ? 'Velja' : 'Choose'}
                </Link>
              </div>
            );

            if (showTrialWarning) return (
              <div
                className={`mb-3 flex items-center gap-2.5 rounded-lg border px-3 py-2 ${
                  isFinalTrialWarning ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'
                }`}
                data-testid="trial-warning-banner"
              >
                <Timer size={16} className={`${isFinalTrialWarning ? 'text-red-300' : 'text-amber-400'} flex-shrink-0`} />
                <p className={`text-sm flex-1 min-w-0 truncate ${isFinalTrialWarning ? 'text-red-100' : 'text-amber-200'}`}>
                  {(() => {
                    const when = daysLeft === 0
                      ? (language === 'en' ? 'today' : 'í dag')
                      : daysLeft === 1
                      ? (language === 'en' ? 'tomorrow' : 'á morgun')
                      : (language === 'en' ? `in ${daysLeft} days` : `eftir ${daysLeft} daga`);
                    return language === 'en' ? `Trial expires ${when}.` : `Prufutími rennur út ${when}.`;
                  })()}
                </p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Link
                    to="/settings/billing"
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md transition-colors whitespace-nowrap ${
                      isFinalTrialWarning
                        ? 'bg-red-500 text-white hover:bg-red-400'
                        : 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                    }`}
                    data-testid="trial-upgrade-btn"
                  >
                    {t('trial_upgrade')}
                  </Link>
                  {!isFinalTrialWarning && (
                    <button
                      onClick={dismissTrialBanner}
                      className="text-amber-400/60 hover:text-amber-400 transition-colors text-xs px-1"
                      data-testid="trial-banner-dismiss"
                      aria-label="Dismiss"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            );

            if (showPlanLimit) return (
              <div className="mb-3 flex items-center gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="plan-limit-warning">
                <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-200 flex-1 min-w-0 truncate">
                  {language === 'en'
                    ? `${currentUsers} of ${maxUsers} users, upgrade plan`
                    : `${currentUsers} af ${maxUsers} notendum, uppfærðu áskrift`}
                </p>
                <Link to="/settings/billing" className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 transition-colors whitespace-nowrap" data-testid="upgrade-plan-btn">
                  {t('plan_upgrade')}
                  <ArrowUpRight size={11} />
                </Link>
              </div>
            );

            return null;
          })()}

          <div key={location.pathname} className="route-fade">
            {children}
          </div>
        </div>
      </main>

      <FloatingActionButton />

      {/* Paywall blocking modal — triggered by 403 paywall_required API responses */}
      <PaywallModal open={showPaywall} onClose={dismissPaywall} />

      {/* Reminder suggestion popup — top on mobile, bottom-right on desktop.
          Suppressed on narrow screens while the trial bar is up (iter324):
          the fixed overlay sat straight on top of it. */}
      {suggestion.show && !(showTrialBar && isNarrowViewport) && (
        <div className="fixed top-16 left-4 right-4 sm:top-auto sm:left-auto sm:bottom-6 sm:right-6 z-50 sm:max-w-xs animate-in slide-in-from-top-4 sm:slide-in-from-bottom-4 fade-in duration-300" data-testid="reminder-suggestion">
          <div className="card p-3 shadow-xl border border-accent/20 flex items-center justify-between gap-3">
            <p className="text-sm font-medium">
              {language === 'is' ? 'Viltu fá áminningu um að skrá tíma?' : 'Want a reminder to log your hours?'}
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={suggestion.enable}
                className="btn btn-primary text-xs px-3 py-1.5"
                data-testid="suggestion-enable"
              >
                {language === 'is' ? 'Virkja' : 'Enable'}
              </button>
              <button
                onClick={suggestion.dismiss}
                className="text-xs px-3 py-1.5 rounded-lg text-foreground-muted hover:text-foreground transition-colors"
                data-testid="suggestion-dismiss"
              >
                {language === 'is' ? 'Ekki núna' : 'Not now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Layout);
